import { useState, useEffect } from "react";
import { useSelector as useReduxSelector, shallowEqual } from "react-redux";
import {
  Drawer,
  Collapse,
  Divider,
  Box,
  Button,
  MenuItem,
  TextField,
  InputBase,
  IconButton,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import TableChartIcon from "@material-ui/icons/TableChart";
import { Link } from "react-router-dom";
import { useSelector, WatchFormActions, useDispatch } from "../state";
import { ScopeActions } from "../state/scopes/actions";
import { Navigation } from "../lib";
import {
  REACT_APP_HOME_SLIDER_ENABLED,
  REACT_APP_OPERATIONS_ENABLED,
  REACT_APP_SITE_HEADER_ENABLED,
} from "../config";
import { PreownedSwitchButton } from "./PreownedSwitchButton";

export const SIDEBAR_WIDTH = 240;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: SIDEBAR_WIDTH,
    background: 'linear-gradient(180deg, #2a4468 0%, #315179 100%)',
    color: '#fff',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderRight: 'none',
    boxShadow: '2px 0 8px rgba(0,0,0,0.18)',
  },
  logoBox: {
    padding: theme.spacing(2, 2, 1.5),
  },
  logoText: {
    fontWeight: 700,
    fontSize: '1.05rem',
    color: '#fff',
    letterSpacing: '0.05em',
  },
  logoSub: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.45)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  /* Marks where catalog administration ends and day-to-day operations begin —
     the same sidebar, but a different job. */
  areaBreak: {
    margin: theme.spacing(1.5, 2, 0.5),
    paddingTop: theme.spacing(1.25),
    borderTop: '1px solid rgba(255,255,255,0.14)',
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.30)',
  },
  /* Menu entries whose screens are not built yet: visible so the shape of the
     area is clear, inert so nothing leads to a dead route.

     Applied ON TOP of subLink rather than restating it, so it picks up
     whichever palette the sidebar uses — this file is forked, and PT's
     sidebar is dark where TPC's is tan. Only the muting is stated here. */
  subLinkSoon: {
    opacity: 0.5,
    cursor: 'default',
    userSelect: 'none',
    '&:hover': { backgroundColor: 'transparent' },
  },
  /* navItem is styled for a div with a chevron; as a link it only needs the
     anchor underline undone. Colour and hover deliberately come from navItem
     itself, so this entry matches the expanding sections on either fork. */
  navLinkItem: {
    textDecoration: 'none',
  },
  addWatchButton: {
    margin: theme.spacing(1.25, 1.5),
    width: `calc(100% - ${theme.spacing(3)}px)`,
    border: '1px solid rgba(255,255,255,0.35)',
    color: '#fff',
    fontSize: '0.82rem',
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: 6,
    '&:hover': {
      border: '1px solid rgba(255,255,255,0.75)',
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.85, 2),
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.87)',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.09)',
    },
  },
  subHeader: {
    padding: theme.spacing(0.75, 2, 0.2, 2.5),
    fontSize: '0.67rem',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.38)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginTop: theme.spacing(0.5),
  },
  subLink: {
    display: 'block',
    padding: theme.spacing(0.5, 2, 0.5, 3),
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.72)',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: '#fff',
    },
  },
  subList: {
    backgroundColor: 'rgba(0,0,0,0.07)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  navScroll: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.2)',
      borderRadius: 2,
    },
  },
  bottomSection: {
    flexShrink: 0,
    padding: theme.spacing(1.5, 1.5, 2),
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  searchPaper: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 6px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    boxShadow: 'none',
    borderRadius: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: '0.82rem',
    color: '#fff',
    '& input': {
      '&::placeholder': {
        color: 'rgba(255,255,255,0.4)',
        opacity: 1,
      },
    },
  },
  searchIconBtn: {
    color: 'rgba(255,255,255,0.55)',
    padding: 4,
    '&:hover': { color: '#fff' },
  },
  siteSelect: {
    width: '100%',
    marginBottom: theme.spacing(1),
    '& .MuiInputBase-root': { color: '#fff', fontSize: '0.82rem' },
    '& .MuiOutlinedInput-root': { borderRadius: 6 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)', borderRadius: 6 },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.55)' },
    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
  },
  logoutBtn: {
    width: '100%',
    color: '#f87171',
    fontSize: '0.82rem',
    textTransform: 'none',
    borderRadius: 6,
    border: '1px solid rgba(248,113,113,0.4)',
    padding: theme.spacing(0.6, 1),
    '&:hover': {
      color: '#fca5a5',
      border: '1px solid rgba(248,113,113,0.75)',
      backgroundColor: 'rgba(248,113,113,0.08)',
    },
  },
}));

function NavSection({ label, open, onToggle, children }) {
  const classes = useStyles();
  return (
    <>
      <div className={classes.navItem} onClick={onToggle}>
        <span>{label}</span>
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </div>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={classes.subList}>{children}</div>
      </Collapse>
    </>
  );
}

function SubHeader({ children }) {
  const classes = useStyles();
  return <div className={classes.subHeader}>{children}</div>;
}

function SubLink({ to, children }) {
  const classes = useStyles();
  return (
    <Link to={to} className={classes.subLink}>
      {children}
    </Link>
  );
}

/** Placeholder entry: shows what is coming without offering a dead link. */
function SubLinkSoon({ children }) {
  const classes = useStyles();
  return (
    <span
      className={`${classes.subLink} ${classes.subLinkSoon}`}
      title="Not built yet"
    >
      {children}
    </span>
  );
}

function AreaBreak({ children }) {
  const classes = useStyles();
  return <div className={classes.areaBreak}>{children}</div>;
}

/** A top-level entry that navigates instead of expanding — no children. */
function NavLinkItem({ to, label }) {
  const classes = useStyles();
  return (
    <Link to={to} className={`${classes.navItem} ${classes.navLinkItem}`}>
      <span>{label}</span>
    </Link>
  );
}

export function Sidebar() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const siteId = useSelector((state) => state.scopes.site_id);
  const { scopes_allowed, selected_scope } = useReduxSelector(
    (state) => state.scopes,
    shallowEqual,
  );

  const [watchOpen, setWatchOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [calibreOpen, setCalibreOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [superAdminOpen, setSuperAdminOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const isSuperAdmin = useReduxSelector(
    (state) => (state.auth?.user?.roles ?? []).includes("super_admin"),
  );
  const [watchSearch, setWatchSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (!selected_scope) {
      dispatch(ScopeActions.getDefaultScope());
    }
  }, [selected_scope]);

  // SPA navigation — WatchesPage listens to router history changes and
  // refreshes the grid in place (no full page reload, no boot splash).
  const handleWatchSearch = () => {
    const term = watchSearch.trim();
    if (term) {
      Navigation.go(`/watches-list?search=${encodeURIComponent(term)}`);
    } else {
      Navigation.go('/watches-list');
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleWatchSearch();
  };

  const updateScope = (i) => {
    dispatch(ScopeActions.updateScope(i));
    setTimeout(() => window.location.reload(true), 1);
  };

  function onLogoutClick() {
    if (!window.confirm("Are you sure you want to log out?")) return;
    Navigation.go("/auth/logout");
  }

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.logoBox}>
        <Typography className={classes.logoText}>Prestige Time</Typography>
        <Typography className={classes.logoSub}>Admin Portal</Typography>
      </Box>

      <PreownedSwitchButton />

      <Divider className={classes.divider} />

      <Box style={{ padding: '10px 12px 4px' }}>
        <Paper className={classes.searchPaper} style={{ border: searchFocused ? '1px solid rgba(255,255,255,0.7)' : undefined }}>
          <InputBase
            className={classes.searchInput}
            placeholder="Search watches…"
            value={watchSearch}
            onChange={(e) => setWatchSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {watchSearch && (
            <IconButton size="small" className={classes.searchIconBtn} onClick={() => setWatchSearch('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" className={classes.searchIconBtn} onClick={handleWatchSearch}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>

      {siteId === 0 && (
        <Button
          variant="outlined"
          className={classes.addWatchButton}
          component={Link}
          to="/add-watch"
          onClick={() => dispatch(WatchFormActions.clearForm())}
        >
          + Add Watch
        </Button>
      )}

      <Button
        variant="outlined"
        className={classes.addWatchButton}
        startIcon={<TableChartIcon style={{ fontSize: 15 }} />}
        style={{ marginTop: 2 }}
        component={Link}
        to="/watches-list"
      >
        Watch Grid
      </Button>

      <Divider className={classes.divider} />

      <div className={classes.navScroll}>
        <NavSection label="Manage Watch" open={watchOpen} onToggle={() => setWatchOpen(!watchOpen)}>
          <SubHeader>Model</SubHeader>
          <SubLink to="/watch-attribute-affiliated/brand">Brands</SubLink>
          <SubLink to="/watch-attribute-affiliated/series">Series</SubLink>
          <SubLink to="/watch-attribute-affiliated/series_group">Series Groups</SubLink>
          <SubLink to="/watch-attributes/size">Size</SubLink>

          <SubHeader>Band & Buckle</SubHeader>
          <SubLink to="/watch-attribute-affiliated/band_color">Band Color</SubLink>
          <SubLink to="/watch-attribute-affiliated/band_material">Band Material</SubLink>
          <SubLink to="/watch-attribute-affiliated/band_name">Band Name</SubLink>
          <SubLink to="/watch-attribute-affiliated/clasp_name">Buckle Name</SubLink>
          <SubLink to="/watch-attributes/clasp_type">Buckle Type</SubLink>
          <SubLink to="/watch-attribute-affiliated/clasp_characteristic">Buckle Characteristic</SubLink>

          <SubHeader>Case</SubHeader>
          <SubLink to="/watch-attributes/bezel">Bezel Type</SubLink>
          <SubLink to="/watch-attributes/bezel_characteristics">Bezel Characteristics</SubLink>
          <SubLink to="/watch-attributes/bezel_color">Bezel Color</SubLink>
          <SubLink to="/watch-attributes/case_back">Case Back</SubLink>
          <SubLink to="/watch-attribute-affiliated/case_material">Material (Case, Crown, etc.)</SubLink>
          <SubLink to="/watch-attributes/case_finish">Finish Extended</SubLink>
          <SubLink to="/watch-attribute-affiliated/case_shape">Case Shape</SubLink>
          <SubLink to="/watch-attributes/crystal">Crystal Material</SubLink>

          <SubHeader>Dial, Subdial & Hour Marker</SubHeader>
          <SubLink to="/watch-attribute-affiliated/dial_color">Dial, Subdial, Dial Flange Color</SubLink>
          <SubLink to="/watch-attributes/date_indicator">Date Indicator</SubLink>
          <SubLink to="/watch-attributes/date_location">Date Location</SubLink>
          <SubLink to="/watch-attributes/flange_indication">Flange Indication</SubLink>
          <SubLink to="/watch-attributes/dial_finish">Main Dial Finish</SubLink>

          <SubLink to="/watch-attributes-finish">Hands & Hour Marker Finish</SubLink>
          <SubLink to="/watch-attributes/hands_color">Hands Color</SubLink>
          <SubLink to="/watch-attribute-affiliated/hands_material">Hands Material</SubLink>
          <SubLink to="/watch-attributes/hour_marker">Hour Markers Characteristics</SubLink>
          <SubLink to="/watch-attributes/hour_marker_color">Hour Markers Color</SubLink>
          <SubLink to="/watch-attributes/hour_marker_type">Hour Markers Type</SubLink>
          <SubLink to="/watch-attributes/second_hand">Second Hand Type</SubLink>
          <SubLink to="/watch-attributes/track_type">Track Type</SubLink>
        </NavSection>

        <NavSection label="Manage Site" open={siteOpen} onToggle={() => setSiteOpen(!siteOpen)}>
          <SubHeader>Vendor</SubHeader>
          <SubLink to="/vendors">Vendors & List Upload</SubLink>

          <SubHeader>Shipping & Warranty</SubHeader>
          <SubLink to="/manage-shipping-tiers/ship_tier">Shipping Tier</SubLink>
          <SubLink to="/manage-site-attributes/delivery">Delivery / Shipping Time Frame</SubLink>
          <SubLink to="/manage-site-attributes/warranty">Warranty</SubLink>

          {REACT_APP_HOME_SLIDER_ENABLED && (
            <>
              <SubHeader>Home Page</SubHeader>
              <SubLink to="/home-slider">Main Slider</SubLink>
              <SubLink to="/home-brand-tiles">Brand Tiles</SubLink>
              <SubLink to="/home-logo-strip">Logo Strip</SubLink>
              <SubLink to="/home-collections">Featured Collections</SubLink>
            </>
          )}

          <SubHeader>Site</SubHeader>
          {REACT_APP_SITE_HEADER_ENABLED && (
            <SubLink to="/site-header">Header / Banner</SubLink>
          )}
          <SubLink to="/search-aliases">Search Aliases</SubLink>
          <SubLink to="/trade-in-brands">Trade-In Brands</SubLink>
          <SubLink to="/url-stubs">URL Stubs</SubLink>
          <SubLink to="/manage-users">Users</SubLink>

          <SubHeader>Reports</SubHeader>
          <SubLink to="/manage-site-attributes/reports">Reports</SubLink>
          <SubLink to="/records">Records</SubLink>

          <SubHeader>Sync</SubHeader>
          {/* <SubLink to="/magento-sync">Magento Sync (Push)</SubLink> */}
          <SubLink to="/watches-pull">TPC Sync Test Run</SubLink>
          <SubLink to="/watchfinder-sync">Watchfinder Sync</SubLink>
        </NavSection>

        <NavSection label="Manage Calibre" open={calibreOpen} onToggle={() => setCalibreOpen(!calibreOpen)}>
          <SubLink to="/movement-attribute/calibre">Calibres</SubLink>
          <SubLink to="/watch-attributes/calibre_complication">Complications</SubLink>
          <SubLink to="/watch-attribute-affiliated/certification">Certifications</SubLink>
          <SubLink to="/watch-attributes/movement">Movement Type</SubLink>
        </NavSection>

        <NavSection label="Manage Groups" open={groupsOpen} onToggle={() => setGroupsOpen(!groupsOpen)}>
          <SubLink to="/watch-attribute-groups/band_color_group">Band Color</SubLink>
          <SubLink to="/watch-attribute-groups/band_material_group">Band Material</SubLink>
          <SubLink to="/watch-attribute-groups/bezel_material_group">Bezel Material</SubLink>
          <SubLink to="/watch-attribute-groups/case_shape_group">Case Shape</SubLink>
          <SubLink to="/watch-attribute-groups/case_material_group">Case Material</SubLink>
          <SubLink to="/watch-attribute-groups/dial_color_group">Dial, Dial Flange Color</SubLink>
        </NavSection>

        {isSuperAdmin && (
          <NavSection label="Super Admin" open={superAdminOpen} onToggle={() => setSuperAdminOpen(!superAdminOpen)}>
            <SubLink to="/mass-update">Mass Warranty/API Update</SubLink>
            <SubLink to="/mass-price-update">Mass Price Update</SubLink>
          </NavSection>
        )}

        {/* Everything above administers the catalog; everything below runs the
            business day to day. PT only — TPC has its own order handling and
            switches this off. */}
        {REACT_APP_OPERATIONS_ENABLED && (
          <>
            <AreaBreak>Operations</AreaBreak>

            {/* One destination, so it navigates rather than expanding. */}
            <NavLinkItem to="/orders" label="Orders" />

            <NavSection
              label="Customer Service"
              open={serviceOpen}
              onToggle={() => setServiceOpen(!serviceOpen)}
            >
              <SubLink to="/repairs">Repairs</SubLink>
              <SubLink to="/returns">Returns / Exchanges</SubLink>
              <SubLink to="/trade-requests">Trade Step 1</SubLink>
              <SubLink to="/trade-ins">Trade Step 2</SubLink>
              <SubLink to="/coupons">Coupons</SubLink>
              <SubLink to="/sales">Sales</SubLink>
              <SubLink to="/accounts">Accounts</SubLink>
            </NavSection>
          </>
        )}
      </div>

      <Box className={classes.bottomSection}>
        <TextField
          select
          value={selected_scope}
          variant="outlined"
          size="small"
          className={classes.siteSelect}
          SelectProps={{
            MenuProps: {
              getContentAnchorEl: null,
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              transformOrigin: { vertical: 'bottom', horizontal: 'right' },
            },
          }}
        >
          {scopes_allowed.map((i) => (
            <MenuItem onClick={() => updateScope(i)} key={i.role} value={i.role}>
              {i.text}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="outlined" className={classes.logoutBtn} onClick={onLogoutClick}>
          Logout
        </Button>
      </Box>
    </Drawer>
  );
}
